import { ReactNode, useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormLabel from '@material-ui/core/FormLabel';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import {
  AddSecretRequest,
  ExternalSecretTarget,
  MAX_VALUE_BYTES,
  VaultSecretRow,
  isNewSecretKey,
  valueByteLength,
  vaultNameFor,
} from '@internal/backstage-plugin-keyvault-secrets-common';

const REFRESH_NOTE = 'The pods get the new value within 1 hour, then restart.';

function valueProblem(value: string): string | undefined {
  if (value === '') {
    return 'Enter a value.';
  }
  const bytes = valueByteLength(value);
  if (bytes > MAX_VALUE_BYTES) {
    return `The value is ${bytes} bytes. Key Vault accepts at most ${MAX_VALUE_BYTES} bytes.`;
  }
  return undefined;
}

function ValueField(props: { value: string; onChange: (value: string) => void }) {
  const problem = props.value === '' ? undefined : valueProblem(props.value);
  return (
    <TextField
      id="keyvault-secret-value"
      label="Value"
      type="password"
      autoComplete="new-password"
      fullWidth
      margin="normal"
      variant="outlined"
      value={props.value}
      onChange={event => props.onChange(event.target.value)}
      error={Boolean(problem)}
      helperText={problem ?? 'Write-only. The portal never shows a value back.'}
    />
  );
}

function WriteDialog(props: {
  title: string;
  submitLabel: string;
  canSubmit: boolean;
  onSubmit: () => Promise<void>;
  onClose: () => void;
  children: ReactNode;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await props.onSubmit();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : props.onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{props.title}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Box pb={2}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
        {props.children}
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!props.canSubmit || busy}
          onClick={submit}
        >
          {props.submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** The value lives only in this component. It is cleared when the request
 * finishes, and the dialog unmounts on close. */
function useSecretValue() {
  const [value, setValue] = useState('');
  const send = async (call: (value: string) => Promise<void>) => {
    try {
      await call(value);
    } finally {
      setValue('');
    }
  };
  return { value, setValue, send };
}

export function EditDialog(props: {
  row: VaultSecretRow;
  onSubmit: (value: string) => Promise<void>;
  onClose: () => void;
}) {
  const { value, setValue, send } = useSecretValue();
  const notInEnv =
    props.row.mappings.length > 0 &&
    props.row.mappings.every(mapping => mapping.env.length === 0);
  return (
    <WriteDialog
      title={`Edit ${props.row.vaultName}`}
      submitLabel="Save new version"
      canSubmit={!valueProblem(value)}
      onSubmit={() => send(props.onSubmit)}
      onClose={props.onClose}
    >
      <Typography variant="body2" paragraph>
        Writes a new version of the secret. {REFRESH_NOTE}
      </Typography>
      {notInEnv && (
        <Alert severity="warning">
          No env variable reads this secret. The pods restart but read nothing new.
        </Alert>
      )}
      <ValueField value={value} onChange={setValue} />
    </WriteDialog>
  );
}

export function AddDialog(props: {
  externalSecrets: ExternalSecretTarget[];
  /** Set for "open mapping PR" on an orphan: the key must map to it. */
  orphanVaultName?: string;
  onSubmit: (request: AddSecretRequest) => Promise<void>;
  onClose: () => void;
}) {
  const { orphanVaultName } = props;
  const [secretKey, setSecretKey] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  const { value, setValue, send } = useSecretValue();
  const main = props.externalSecrets.filter(target => target.component);

  const vaultName = vaultNameFor(secretKey);
  let keyProblem: string | undefined;
  if (secretKey !== '' && !isNewSecretKey(secretKey)) {
    keyProblem =
      'Use letters, digits, _ and -, starting with a letter or _. The secret name must be at most 127 characters.';
  } else if (orphanVaultName && secretKey !== '' && vaultName !== orphanVaultName) {
    keyProblem = `This name gives the secret ${vaultName}, not ${orphanVaultName}.`;
  }

  const keyHint = orphanVaultName
    ? `A name that gives the secret ${orphanVaultName}, for example ${orphanVaultName.toUpperCase().replace(/-/g, '_')}.`
    : 'For example SendGrid__ApiKey.';

  const toggle = (id: string, checked: boolean) =>
    setChosen(current =>
      checked ? [...current, id] : current.filter(other => other !== id),
    );

  return (
    <WriteDialog
      title={orphanVaultName ? `Add ${orphanVaultName} to the app` : 'Add secret'}
      submitLabel={orphanVaultName ? 'Open PR' : 'Add secret'}
      canSubmit={
        secretKey !== '' && !keyProblem && chosen.length > 0 && !valueProblem(value)
      }
      onSubmit={() =>
        send(secretValue =>
          props.onSubmit({ secretKey, externalSecretIds: chosen, value: secretValue }),
        )
      }
      onClose={props.onClose}
    >
      <Typography variant="body2" paragraph>
        Writes the value to the vault and opens a gitops-platform pull request
        that adds the env variable to the chosen components. After the PR merges,
        the pods get it within 1 hour.
      </Typography>
      <TextField
        id="keyvault-secret-key"
        label="Env variable"
        fullWidth
        margin="normal"
        variant="outlined"
        value={secretKey}
        onChange={event => setSecretKey(event.target.value)}
        error={Boolean(keyProblem)}
        helperText={
          keyProblem ??
          (secretKey === '' ? keyHint : `Secret name: ${vaultName}`)
        }
      />
      <Box pt={1}>
        <FormLabel component="legend">Components</FormLabel>
        {main.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            The portal cannot add env variables in this stage. Ask the platform team for a manual PR.
          </Typography>
        ) : (
          <FormGroup>
            {main.map(target => (
              <FormControlLabel
                key={target.externalSecretId}
                control={
                  <Checkbox
                    checked={chosen.includes(target.externalSecretId)}
                    onChange={event => toggle(target.externalSecretId, event.target.checked)}
                  />
                }
                label={target.component}
              />
            ))}
          </FormGroup>
        )}
      </Box>
      <ValueField value={value} onChange={setValue} />
    </WriteDialog>
  );
}

export function RemoveDialog(props: {
  row: VaultSecretRow;
  externalSecrets: ExternalSecretTarget[];
  onSubmit: (mappingIds: string[]) => Promise<void>;
  onClose: () => void;
}) {
  const [chosen, setChosen] = useState<string[]>([]);
  const targetOf = (externalSecretId: string) =>
    props.externalSecrets.find(target => target.externalSecretId === externalSecretId);

  const toggle = (id: string, checked: boolean) =>
    setChosen(current =>
      checked ? [...current, id] : current.filter(other => other !== id),
    );

  return (
    <WriteDialog
      title={`Remove ${props.row.vaultName}`}
      submitLabel="Open remove PR"
      canSubmit={chosen.length > 0}
      onSubmit={() => props.onSubmit(chosen)}
      onClose={props.onClose}
    >
      <Typography variant="body2" paragraph>
        Opens a gitops-platform pull request that removes the env variable from
        the chosen components. The secret stays enabled in the vault. Disable it
        after the PR merges and the pods sync (within 1 hour).
      </Typography>
      <FormGroup>
        {props.row.mappings.map(mapping => {
          const target = targetOf(mapping.externalSecretId);
          const main = Boolean(target?.component);
          return (
            <FormControlLabel
              key={mapping.mappingId}
              disabled={!main}
              control={
                <Checkbox
                  checked={chosen.includes(mapping.mappingId)}
                  onChange={event => toggle(mapping.mappingId, event.target.checked)}
                />
              }
              label={`${mapping.secretKey} (${
                main ? target?.component : 'needs a manual PR'
              })`}
            />
          );
        })}
      </FormGroup>
    </WriteDialog>
  );
}

export function ConfirmDialog(props: {
  title: string;
  text: string;
  submitLabel: string;
  onSubmit: () => Promise<void>;
  onClose: () => void;
}) {
  return (
    <WriteDialog
      title={props.title}
      submitLabel={props.submitLabel}
      canSubmit
      onSubmit={props.onSubmit}
      onClose={props.onClose}
    >
      <Typography variant="body2">{props.text}</Typography>
    </WriteDialog>
  );
}
